import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/connect/RichTextEditor";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy, Archive, ArrowCounterClockwise, Star, PencilSimple, DeviceMobile, Desktop, Eye, CheckCircle as CheckCircleIcon } from "@phosphor-icons/react";

const CAT_LABELS = {
  payment_reminder: "Payment Reminder", due_today: "Due Today", overdue: "Overdue",
  payment_confirmation: "Payment Confirmation", welcome: "Welcome", settlement: "Settlement",
  salary_day_reminder: "Salary Day Reminder", reloan_eligibility: "Re-Loan Eligibility",
  general: "General", custom: "Custom",
};

export default function EmailTemplates() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [vars, setVars] = useState([]);
  const [cats, setCats] = useState([]);
  const [editor, setEditor] = useState(null); // {template|null}

  const load = useCallback(async () => {
    try {
      const [t, v] = await Promise.all([
        api.get("/connect/email/studio/templates"),
        api.get("/connect/email/variables"),
      ]);
      setRows(t.data.templates || []);
      setVars(v.data.variables || []);
      setCats(v.data.categories || []);
    } catch { toast.error("Could not load templates"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try { await api.post(`/connect/email/studio/templates/${id}/${action}`, {}); toast.success(`Template ${action}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const active = rows.filter((r) => r.status === "active");
  const archived = rows.filter((r) => r.status === "archived");

  return (
    <div data-testid="email-templates-page">
      <button onClick={() => nav("/connect")} className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground" data-testid="templates-back">
        <ArrowLeft size={14} weight="bold" /> Back to Connect
      </button>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Create reusable, branded emails. Formatting and variables are preserved and versioned.</p>
        </div>
        <Button className="rounded-sm" onClick={() => setEditor({ template: null })} data-testid="templates-new">
          <Plus size={16} weight="bold" className="mr-1.5" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-list">
        {active.map((t) => (
          <div key={t.id} className="border border-border rounded-sm p-4 bg-card flex flex-col" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold truncate flex items-center gap-1.5">
                  {t.name}
                  {t.is_default && <Star size={13} weight="fill" className="text-amber-500" title="Default" />}
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground mt-1 inline-block">{CAT_LABELS[t.category] || t.category}</span>
              </div>
              <span className="text-[10px] text-muted-foreground mono shrink-0">v{t.version}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.subject}</div>
            <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-1">
              <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 text-xs" onClick={() => setEditor({ template: t })} data-testid={`template-edit-${t.id}`}><PencilSimple size={13} className="mr-1" />Edit</Button>
              <Button size="sm" variant="ghost" className="rounded-sm h-7 px-2 text-xs" onClick={() => act(t.id, "duplicate")} data-testid={`template-dup-${t.id}`}><Copy size={13} /></Button>
              {!t.is_default && <Button size="sm" variant="ghost" className="rounded-sm h-7 px-2 text-xs" onClick={() => act(t.id, "default")} data-testid={`template-default-${t.id}`}><Star size={13} /></Button>}
              <Button size="sm" variant="ghost" className="rounded-sm h-7 px-2 text-xs text-destructive" onClick={() => act(t.id, "archive")} data-testid={`template-archive-${t.id}`}><Archive size={13} /></Button>
            </div>
          </div>
        ))}
      </div>

      {archived.length > 0 && (
        <div className="mt-6">
          <div className="label-caps mb-2">Archived</div>
          <div className="flex flex-wrap gap-2">
            {archived.map((t) => (
              <div key={t.id} className="flex items-center gap-2 border border-border rounded-sm px-3 py-1.5 text-xs" data-testid={`template-archived-${t.id}`}>
                <span className="text-muted-foreground">{t.name}</span>
                <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" onClick={() => act(t.id, "activate")} data-testid={`template-restore-${t.id}`}><ArrowCounterClockwise size={12} className="mr-1" />Restore</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {editor && (
        <TemplateEditor template={editor.template} vars={vars} cats={cats}
          onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />
      )}
    </div>
  );
}

function TemplateEditor({ template, vars, cats, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || "general");
  const [subject, setSubject] = useState(template?.subject || "");
  const [html, setHtml] = useState(template?.html || "<p>Dear {{customer_name}},</p><p></p>");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("desktop");

  const doPreview = async () => {
    try {
      const { data } = await api.post("/connect/email/studio/preview", { subject, html, category });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const save = async () => {
    if (!name.trim() || !subject.trim()) { toast.error("Name and subject are required"); return; }
    setSaving(true);
    try {
      const body = { name, category, subject, html, design_json: { html } };
      if (template) await api.put(`/connect/email/studio/templates/${template.id}`, body);
      else await api.post("/connect/email/studio/templates", body);
      toast.success(template ? "Template updated (new version saved)" : "Template created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader><DialogTitle>{template ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1" data-testid="template-name" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="template-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c] || c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm mt-1" placeholder="Payment reminder for {{reference_number}}" data-testid="template-subject" />
            </div>
            <div>
              <Label className="text-xs">Body</Label>
              <div className="mt-1"><RichTextEditor value={html} onChange={setHtml} variables={vars} testid="template-rte" /></div>
            </div>
            <Button variant="outline" className="rounded-sm" onClick={doPreview} data-testid="template-preview-btn"><Eye size={14} className="mr-1.5" />Update Preview</Button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-caps">Preview</div>
              <div className="flex gap-1">
                <Button size="sm" variant={mode === "desktop" ? "default" : "outline"} className="rounded-sm h-7 px-2" onClick={() => setMode("desktop")} data-testid="preview-desktop"><Desktop size={14} /></Button>
                <Button size="sm" variant={mode === "mobile" ? "default" : "outline"} className="rounded-sm h-7 px-2" onClick={() => setMode("mobile")} data-testid="preview-mobile"><DeviceMobile size={14} /></Button>
              </div>
            </div>
            {preview ? (
              <div className="border border-border rounded-sm bg-slate-100 p-3">
                <div className="text-xs mb-1"><span className="text-muted-foreground">From:</span> {preview.sending_as}</div>
                <div className="text-xs mb-1 flex items-center gap-1"><span className="text-muted-foreground">Mode:</span> <CheckCircleIcon size={12} weight="fill" className="text-emerald-600" /> {preview.mode_label}</div>
                <div className="text-xs mb-1"><span className="text-muted-foreground">Branding:</span> v{preview.branding_version ?? "-"}</div>
                <div className="text-xs mb-2"><span className="text-muted-foreground">Subject:</span> <span className="font-semibold">{preview.subject}</span></div>
                <div className="mx-auto bg-white" style={{ width: mode === "mobile" ? 360 : "100%" }}>
                  <iframe title="preview" srcDoc={preview.html} className="w-full h-[440px] border-0" data-testid="preview-iframe" />
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-border rounded-sm p-8 text-center text-sm text-muted-foreground">Click "Update Preview" to see the complete rendered email with your branding and sample data.</div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="template-cancel">Cancel</Button>
          <Button className="rounded-sm" onClick={save} disabled={saving} data-testid="template-save">{saving ? "Saving…" : (template ? "Save New Version" : "Create Template")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
